import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import ToolbarDivider from '~/components/toolbar/shared/ToolbarDivider';
import ToolPropertiesTray from '~/components/toolbar/shared/ToolPropertiesTray';
import AutoRegisterTool from '~/components/toolbar/tools/AutoRegisterTool';
import AutoRegisterOptions from '~/components/toolbar/tools/AutoRegisterTool/AutoRegisterOptions';
import BoxZoomTool from '~/components/toolbar/tools/BoxZoomTool';
import PanTool from '~/components/toolbar/tools/PanTool';
import RegistrationInvertTool from '~/components/toolbar/tools/RegistrationInvertTool';
import RegistrationModeTool from '~/components/toolbar/tools/RegistrationModeTool';
import RegistrationUndoRedoTool from '~/components/toolbar/tools/RegistrationUndoRedoTool';
import ResetViewTool from '~/components/toolbar/tools/ResetViewTool';
import { RegistrationMode } from '~/engine/tool/tools/RegistrationTool';
import { useRegistrationState } from '~/hooks/registration/useRegistrationStore';
import { secondaryImageState } from '~/state/atoms/image/imageAtoms';
import {
    autoRegistrationInProgressState,
    registrationModeState,
    selectedToolState,
    voiCheckboxState,
} from '~/state/atoms/toolsAtoms';

export default function RegistrationTools() {
    const registrationState = useRegistrationState();
    const secondaryImage = useRecoilValue(secondaryImageState);
    const [tool, setTool] = useRecoilState(selectedToolState);
    const [, setRegistrationMode] = useRecoilState(registrationModeState);
    const [isAutoRegisterOpen, setIsAutoRegisterOpen] = useState(false);
    const setVoiCheckbox = useSetRecoilState(voiCheckboxState);
    const isAutoRegistering = useRecoilValue(autoRegistrationInProgressState);

    const hasSecondaryImage =
        !!secondaryImage || !!registrationState.selectedSopInstanceUid;
    const isApproved =
        registrationState.selectedRegistration?.isApproved ?? false;
    const isDisabled = !hasSecondaryImage || isApproved || isAutoRegistering;

    // Manage tool state based on secondary image and reg approval status
    useEffect(() => {
        if (!hasSecondaryImage) {
            return;
        }
        if (isApproved) {
            setTool('pan');
            setIsAutoRegisterOpen(false);
            setVoiCheckbox(false);
        } else {
            setTool('registration');
            setRegistrationMode((prev) => ({
                ...prev,
                mode: prev.mode ?? RegistrationMode.TRANSLATION,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasSecondaryImage, isApproved]);

    return (
        <>
            <PanTool />
            <BoxZoomTool />
            <ResetViewTool />
            <ToolbarDivider />
            <RegistrationModeTool
                isDisabled={isDisabled}
                onModeSelected={() => {
                    setIsAutoRegisterOpen(false);
                    setVoiCheckbox(false);
                }}
            />
            <AutoRegisterTool
                isDisabled={isDisabled}
                isOpen={isAutoRegisterOpen && tool === 'registration'}
                onToggle={() => {
                    const opening =
                        !isAutoRegisterOpen || tool !== 'registration';
                    setIsAutoRegisterOpen(opening);
                    setVoiCheckbox(opening);
                    if (opening) {
                        setRegistrationMode((m) => ({
                            ...m,
                            mode: RegistrationMode.VOI,
                        }));
                        setTool('registration');
                    }
                }}
            />
            <RegistrationInvertTool isDisabled={isDisabled} />
            <ToolbarDivider />
            <RegistrationUndoRedoTool isDisabled={isDisabled} />
            {isAutoRegisterOpen && tool === 'registration' && (
                <ToolPropertiesTray>
                    <AutoRegisterOptions isDisabled={isDisabled} />
                </ToolPropertiesTray>
            )}
        </>
    );
}
